import React, { useState, useEffect } from 'react';
import pb from '../../../lib/pocketbase';
import './HomeBooking.css';

const HomeBooking = ({ user, onLoginClick, agencySettings }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [availableDates, setAvailableDates] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);

    const [bookingData, setBookingData] = useState({
        service: '',
        date: '',
        time: '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        phone: user?.phone || '',
        email: user?.email || '',
        vehicle_details: '',
        createAccount: false,
        password: ''
    });

    const services = [
        { id: 'cle', label: 'Clés', icon: '🔑' },
        { id: 'ethanol', label: 'Éthanol', icon: '⛽' },
        { id: 'diag', label: 'Diag', icon: '💻' },
    ];

    useEffect(() => {
        const h = agencySettings?.opening_hours ? (typeof agencySettings.opening_hours === 'string' ? JSON.parse(agencySettings.opening_hours) : agencySettings.opening_hours) : null;
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        
        const dates = [];
        let curr = new Date();
        curr.setHours(0, 0, 0, 0);
        
        // Show next 14 valid days
        let count = 0;
        while (dates.length < 14 && count < 30) {
            curr.setDate(curr.getDate() + 1);
            count++;
            
            const dayName = dayNames[curr.getDay()];
            const isOpen = h ? h[dayName]?.open : curr.getDay() !== 0; // fallback to skipping sundays
            
            if (isOpen) {
                dates.push(new Date(curr));
            }
        }
        setAvailableDates(dates);
    }, [agencySettings]);

    const handleDateSelect = async (dateObj) => {
        const dateStr = dateObj.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
        setBookingData({ ...bookingData, date: dateStr });
        setLoading(true);

        const h = agencySettings?.opening_hours ? (typeof agencySettings.opening_hours === 'string' ? JSON.parse(agencySettings.opening_hours) : agencySettings.opening_hours) : null;
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = dayNames[dateObj.getDay()];
        const daySet = h ? h[dayName] : null;

        try {
            const existing = await pb.collection('appointments').getList(1, 100, { filter: `date = "${dateStr}"` });
            const booked = existing.items.map(a => a.time);

            let slots = [];
            // If Agency is in busy mode or day is closed, no slots
            if (agencySettings?.busy_mode_active || (daySet && !daySet.open)) {
                slots = [];
            } else if (daySet && daySet.start && daySet.end) {
                // Generate slots based on admin hours (ex: 8:00 - 18:00)
                const startH = parseInt(daySet.start.split(':')[0]);
                const endH = parseInt(daySet.end.split(':')[0]);
                for (let hr = startH; hr < endH; hr++) {
                    const timeStr = `${hr.toString().padStart(2, '0')}:00`;
                    slots.push(timeStr);
                }
            } else {
                // Default slots
                slots = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'];
            }

            setAvailableSlots(slots.filter(s => !booked.includes(s)));
            setStep(3);
        } catch (e) {
            setStep(3);
        } finally {
            setLoading(false);
        }
    };

    const handleFinalSubmit = async () => {
        setLoading(true);
        try {
            let userId = user?.id;

            // Handle account creation if guest wants to
            if (!user && bookingData.createAccount) {
                const userData = {
                    email: bookingData.email,
                    password: bookingData.password,
                    passwordConfirm: bookingData.password,
                    name: `${bookingData.firstName} ${bookingData.lastName}`,
                    firstName: bookingData.firstName,
                    lastName: bookingData.lastName,
                    phone: bookingData.phone
                };
                const newUser = await pb.collection('users').create(userData);
                userId = newUser.id;
                // Auto-login or just use this ID for appointment
            }

            const appointment = {
                users: userId || null,
                service: bookingData.service,
                date: bookingData.date,
                time: bookingData.time,
                firstName: bookingData.firstName,
                lastName: bookingData.lastName,
                email: bookingData.email,
                phone: bookingData.phone,
                vehicle_details: bookingData.vehicle_details,
                status: 'pending'
            };

            await pb.collection('appointments').create(appointment);
            setStep(5);
        } catch (err) {
            alert('Erreur : ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="home-booking-section">
            <div className="booking-glass-card fadeIn">
                <div className="booking-header-home">
                    <h3>{step === 5 ? 'Félicitations !' : 'Réserver une intervention'}</h3>
                    <p>{step === 5 ? 'Votre rendez-vous est confirmé.' : 'Réservez en 1 minute, avec ou sans compte.'}</p>
                </div>

                <div className="home-booking-stepper">
                    {[1, 2, 3, 4].map(s => (
                        <div key={s} className={`h-step ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''}`}></div>
                    ))}
                </div>

                <div className="home-booking-body">
                    {agencySettings?.closure_banner_active && step < 5 && (
                        <div className="h-closure-banner animation-fadeIn">
                            <span className="h-cb-icon">📅</span>
                            <div className="h-cb-text">
                                <strong>Information Atelier</strong>
                                <p>{agencySettings.closure_auto_responder || "L'atelier est actuellement fermé. Les réservations sont temporairement indisponibles."}</p>
                            </div>
                        </div>
                    )}
                    {loading && <div className="h-loader">Chargement...</div>}

                    {step === 1 && (
                        <div className="h-step-content animation-fadeIn">
                            <span className="h-label">Choisissez un service</span>
                            <div className="h-services-grid">
                                {services.map(s => (
                                    <button
                                        key={s.id}
                                        className={`h-service-btn ${bookingData.service === s.id ? 'selected' : ''}`}
                                        onClick={() => { setBookingData({ ...bookingData, service: s.id }); setStep(2); }}
                                    >
                                        <span className="h-s-icon">{s.icon}</span>
                                        <span className="h-s-label">{s.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="h-step-content animation-fadeIn">
                            <button className="h-back" onClick={() => setStep(1)}>← Retour</button>
                            <span className="h-label">Choisissez une date</span>
                            <div className="h-dates-scroll">
                                {availableDates.map((d, i) => (
                                    <button key={i} className="h-date-card" onClick={() => handleDateSelect(d)}>
                                        <span className="h-d-day">{d.toLocaleDateString('fr-FR', { weekday: 'short' })}</span>
                                        <span className="h-d-num">{d.getDate()}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="h-step-content animation-fadeIn">
                            <button className="h-back" onClick={() => setStep(2)}>← Retour</button>
                            <span className="h-label">Choisissez un horaire</span>
                            <div className="h-time-grid">
                                {availableSlots.map(s => (
                                    <button key={s} className="h-time-btn" onClick={() => { setBookingData({ ...bookingData, time: s }); setStep(4); }}>{s}</button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="h-step-content animation-fadeIn">
                            <button className="h-back" onClick={() => setStep(3)}>← Retour</button>
                            {!user && (
                                <div className="h-login-shortcut">
                                    Déjà client ? <button className="h-inline-link" onClick={onLoginClick}>Connectez-vous</button>
                                </div>
                            )}

                            <div className="h-form-grid">
                                <div className="h-field">
                                    <label>Prénom & Nom</label>
                                    <input
                                        type="text"
                                        placeholder="Jean Dupont"
                                        value={user ? `${user.firstName || ''} ${user.lastName || ''}` : `${bookingData.firstName} ${bookingData.lastName}`}
                                        onChange={e => {
                                            const parts = e.target.value.split(' ');
                                            setBookingData({ ...bookingData, firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '' });
                                        }}
                                        disabled={!!user}
                                    />
                                </div>
                                <div className="h-field">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        placeholder="jean@exemple.fr"
                                        value={user ? user.email : bookingData.email}
                                        onChange={e => setBookingData({ ...bookingData, email: e.target.value })}
                                        disabled={!!user}
                                    />
                                </div>
                                <div className="h-field">
                                    <label>Téléphone</label>
                                    <input
                                        type="tel"
                                        placeholder="06..."
                                        value={bookingData.phone}
                                        onChange={e => setBookingData({ ...bookingData, phone: e.target.value })}
                                    />
                                </div>
                                <div className="h-field">
                                    <label>Véhicule (ex: Peugeot 208)</label>
                                    <input
                                        type="text"
                                        placeholder="Marque & Modèle"
                                        value={bookingData.vehicle_details}
                                        onChange={e => setBookingData({ ...bookingData, vehicle_details: e.target.value })}
                                    />
                                </div>
                            </div>

                            {!user && (
                                <div className="h-account-opt">
                                    <label className="h-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={bookingData.createAccount}
                                            onChange={e => setBookingData({ ...bookingData, createAccount: e.target.checked })}
                                        />
                                        <span>Créer un compte pour suivre mon RDV</span>
                                    </label>
                                    {bookingData.createAccount && (
                                        <div className="h-field animation-fadeIn" style={{ marginTop: '0.5rem' }}>
                                            <label>Choisissez un mot de passe</label>
                                            <input
                                                type="password"
                                                placeholder="••••••••"
                                                value={bookingData.password}
                                                onChange={e => setBookingData({ ...bookingData, password: e.target.value })}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            <button
                                className="h-submit-btn"
                                onClick={handleFinalSubmit}
                                disabled={!bookingData.phone || !bookingData.email || (bookingData.createAccount && !bookingData.password)}
                            >
                                Confirmer le rendez-vous
                            </button>
                        </div>
                    )}

                    {step === 5 && (
                        <div className="h-step-content animation-fadeIn h-success">
                            <div className="h-success-icon">✓</div>
                            <p>Merci pour votre confiance ! Un email récapitulatif vous a été envoyé.</p>
                            <button className="h-reset" onClick={() => setStep(1)}>Réserver un autre créneau</button>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default HomeBooking;
